import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Save, Sparkles, X, Flame, RotateCcw, Sunrise, Moon, CalendarRange, Hammer } from 'lucide-react';
import LIBRARY from '../../data/Imperial_Sovereignty_Library.json';
import { pingAuraFuse } from '../../utils/auraPing';
import { vibrate as nativeVibrate } from '../../utils/haptics';

/* ═══════════════════════════════════════════════════════════════════
   AEGIS · REGIMEN SCHEDULE (v3 — 3-col tabulation + Forge cycle)
   • Per-category (skin | hair) — accessed via own master card.
   • Compact 3-column table: Care | Product | Nutrition.
   • Default Nutrition auto-seeded (user can edit).
   • Bottom "Forge This Regimen" → picks 21/30/66/90/100/180-day cycle.
     Calendar grid tracks daily completion.
   • Zero-cost. localStorage only.
   ═══════════════════════════════════════════════════════════════════ */

const NAVY    = '#050507';
const NAVY_HI = '#0E0E14';
const GOLD    = '#D4AF37';
const GOLD_HI = '#FFE9A8';
const GOLD_LO = '#7A5418';

export const REGIMEN_KEY = 'aegis_regimen_v1';

/* ═══════════════════════════════════════════════════════════════════
   Forge day cell — long-press (700ms) to permanently fuse a day.
   Fused days cannot be un-fused. Shows a gold-ring charge while held.
   ═══════════════════════════════════════════════════════════════════ */
const CHARGE_MS = 700;
const ForgeDayCell = ({ day, isFused, onFuse }) => {
  const [pct, setPct] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const firedRef = useRef(false);

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setPct(0);
  };

  const begin = (e) => {
    if (isFused) return;
    e.preventDefault();
    firedRef.current = false;
    startRef.current = performance.now();
    const tick = () => {
      const p = Math.min(1, (performance.now() - startRef.current) / CHARGE_MS);
      setPct(p);
      if (p >= 1) {
        firedRef.current = true;
        stop();
        onFuse(day);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const cancel = () => { if (!firedRef.current) stop(); };

  useEffect(() => () => stop(), []);

  return (
    <button type="button"
      onPointerDown={begin}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
      disabled={isFused}
      data-testid={`aegis-forge-day-${day}`}
      className="relative aspect-square rounded-md flex items-center justify-center overflow-hidden select-none"
      style={{
        background: isFused
          ? `linear-gradient(155deg, ${GOLD_HI}, ${GOLD_LO})`
          : `linear-gradient(160deg, ${NAVY_HI}, ${NAVY})`,
        border: `1px solid ${isFused ? GOLD_HI : `${GOLD}44`}`,
        boxShadow: isFused
          ? `0 0 8px ${GOLD}77, inset 0 0 6px rgba(255,255,255,0.2)`
          : (pct > 0 ? `0 0 10px ${GOLD}AA, inset 0 0 6px ${GOLD}55` : 'none'),
        color: isFused ? '#050507' : GOLD_HI,
        fontSize: 10,
        cursor: isFused ? 'default' : 'pointer',
        transform: pct > 0 ? `scale(${1 + pct * 0.06})` : 'scale(1)',
        transition: 'transform 60ms linear, box-shadow 120ms linear',
        touchAction: 'none',
        WebkitUserSelect: 'none',
      }}>
      {/* Charge fill — rises from bottom while holding */}
      {!isFused && pct > 0 && (
        <span aria-hidden
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            height: `${pct * 100}%`,
            background: `linear-gradient(180deg, ${GOLD}CC, ${GOLD_HI})`,
            opacity: 0.55, pointerEvents: 'none',
          }}/>
      )}
      <span style={{ position: 'relative', zIndex: 1 }}>{day}</span>
    </button>
  );
};

/* Care rows + default nutrition per slot ─────────────────────────── */
const TEMPLATE = {
  skin: {
    am: [
      { id: 'cleanse',        care: 'Cleanser',   defaultNutrition: 'Water 2L, green tea, cucumber' },
      { id: 'serum',          care: 'Serum',      defaultNutrition: 'Vit C (citrus, kiwi, berries)' },
      { id: 'moisturizer',    care: 'Moisture',   defaultNutrition: 'Omega-3 (walnuts, chia)' },
      { id: 'sunscreen',      care: 'Sunscreen',  defaultNutrition: 'Beta-carotene (carrots, sweet potato)' },
    ],
    pm: [
      { id: 'cleanser',       care: 'Cleanser',   defaultNutrition: 'Water, cucumber, watermelon' },
      { id: 'serum_pm',       care: 'Serum',      defaultNutrition: 'Berries, dark chocolate, Vit C' },
      { id: 'moisturizer_pm', care: 'Moisture',   defaultNutrition: 'Avocado, olive oil, Vit E' },
      { id: 'seal',           care: 'Seal',       defaultNutrition: 'Bone broth, collagen 10g' },
    ],
    weekly: [
      { id: 'exfoliation',    care: 'Exfoliation',    defaultNutrition: 'Zinc (pumpkin seeds), Vit A' },
      { id: 'skin_deep_mask', care: 'Deep Mask',      defaultNutrition: 'Aloe, hyaluronic (bone broth)' },
    ],
  },
  hair: {
    am: [
      { id: 'detangle',        care: 'Detangle',      defaultNutrition: 'Protein 1.2g/kg, biotin' },
      { id: 'scalp_stimulant', care: 'Scalp Stim',    defaultNutrition: 'Iron, Vit D3 2000 IU' },
      { id: 'uv_shield',       care: 'UV Shield',     defaultNutrition: 'Vit E (almonds), beta-carotene' },
    ],
    pm: [
      { id: 'scalp_cleanser',  care: 'Scalp Wash',    defaultNutrition: 'Zinc, B-complex' },
      { id: 'target_treatment',care: 'Targeted',      defaultNutrition: 'Biotin, Iron, Vit D3' },
      { id: 'repair_nourish',  care: 'Repair Oil',    defaultNutrition: 'Omega-3 (salmon, chia)' },
      { id: 'restoration',     care: 'Massage',       defaultNutrition: 'Magnesium (leafy greens)' },
    ],
    weekly: [
      { id: 'scalp_treatment', care: 'Scalp Mask',    defaultNutrition: 'Zinc 15mg, Omega-3 1g' },
      { id: 'hair_deep_oil',   care: 'Deep Oil',      defaultNutrition: 'Vit E, biotin, silica (oats)' },
    ],
  },
  body: {
    am: [
      { id: 'body_cleanse',    care: 'Body Wash',     defaultNutrition: 'Water 2L, greens, hydration' },
      { id: 'body_serum',      care: 'Body Serum',    defaultNutrition: 'Vit C (citrus, kiwi)' },
      { id: 'body_moisture',   care: 'Body Lotion',   defaultNutrition: 'Omega-3, avocado, Vit E' },
      { id: 'body_spf',        care: 'Body SPF',      defaultNutrition: 'Beta-carotene (carrots, sweet potato)' },
    ],
    pm: [
      { id: 'body_cleanse_pm', care: 'Body Wash',     defaultNutrition: 'Water, watermelon, chlorophyll' },
      { id: 'body_treatment',  care: 'Body Treat',    defaultNutrition: 'Berries, dark chocolate, Vit C' },
      { id: 'body_seal',       care: 'Body Cream',    defaultNutrition: 'Bone broth, collagen 10g' },
      { id: 'body_oil',        care: 'Body Oil',      defaultNutrition: 'Omega-3, chia, Vit E' },
    ],
    weekly: [
      { id: 'body_exfoliation',care: 'Body Scrub',    defaultNutrition: 'Zinc 15mg, hydration' },
      { id: 'body_brush',      care: 'Dry Brush',     defaultNutrition: 'Magnesium, potassium (leafy greens)' },
    ],
  },
};

const FORGE_PERIODS = [
  { days: 21,  label: '21 · 3 Weeks' },
  { days: 30,  label: '30 · 1 Month' },
  { days: 66,  label: '66 · Habit' },
  { days: 90,  label: '90 · 3 Months' },
  { days: 100, label: '100 · Century' },
  { days: 180, label: '180 · 6 Months' },
];

const buildInitial = (persisted) => {
  const seed = (arr, prev) => arr.map((slot) => {
    const p = prev?.find((s) => s.id === slot.id);
    return {
      id: slot.id,
      care: p?.care || slot.care,
      product: p?.product ?? p?.chemical ?? '',                 // migrate old field name
      nutrition: p?.nutrition ?? p?.nutritional ?? slot.defaultNutrition, // seed with default
    };
  });
  const seedCat = (cat, prev) => ({
    am:     seed(cat.am,     prev?.am),
    pm:     seed(cat.pm,     prev?.pm),
    weekly: seed(cat.weekly, prev?.weekly),
    forge:  prev?.forge || null,
  });
  return {
    skin: seedCat(TEMPLATE.skin, persisted?.skin),
    hair: seedCat(TEMPLATE.hair, persisted?.hair),
    body: seedCat(TEMPLATE.body, persisted?.body),
  };
};

const loadRegimen = () => {
  try {
    const raw = localStorage.getItem(REGIMEN_KEY);
    return raw ? buildInitial(JSON.parse(raw)) : buildInitial();
  } catch (_) { return buildInitial(); }
};
const saveRegimen = (r) => {
  try { localStorage.setItem(REGIMEN_KEY, JSON.stringify(r)); } catch (_) { /* quota */ }
};

const haptic = (ms = 10) => {
  try { nativeVibrate(ms); } catch (_) { /* no-op */ }
};

/* Suggest nutritional from library based on product text (case-safe) */
export const suggestNutritional = (productText, category) => {
  const q = (productText || '').toLowerCase().trim();
  if (q.length < 3) return null;
  const cat = (category || '').toLowerCase();
  const words = q.split(/[\s,+/()]+/).filter((w) => w.length >= 3);
  let best = null; let bestScore = 0;
  const pool = LIBRARY.filter((e) => (e.Category || '').toLowerCase() === cat);
  for (const entry of pool) {
    const chem = (entry.Chemical_Solution || '').toLowerCase();
    let s = 0;
    for (const w of words) {
      if (chem.includes(w)) s += 3;
      if ((entry.Keyword || '').toLowerCase().includes(w)) s += 2;
    }
    if (s > bestScore) { bestScore = s; best = entry; }
  }
  return best ? { nutritional: best.Nutritional_Solution, source: best.Keyword } : null;
};

const searchLibrary = (query, category) => {
  const q = (query || '').trim().toLowerCase();
  const cat = (category || '').toLowerCase();
  const pool = LIBRARY.filter((e) => (e.Category || '').toLowerCase() === cat);
  if (!q) return pool.slice(0, 12);
  return pool
    .filter((e) =>
      e.Keyword.toLowerCase().includes(q) ||
      (e.Aliases || []).some((a) => a.toLowerCase().includes(q)) ||
      (e.Chemical_Solution || '').toLowerCase().includes(q)
    )
    .slice(0, 15);
};

/* ═══════════════════════════════════════════════════════════════════
   TABLE ROW — Care | Product | Nutrition
   ═══════════════════════════════════════════════════════════════════ */
const TableRow = ({ slot, category, onChange, onOpenPicker }) => {
  const testKey = `${category}-${slot.id}`;
  return (
    <div className="grid grid-cols-[70px_1fr_1fr] gap-1.5 items-start py-1.5 px-1"
      style={{ borderTop: `1px solid ${GOLD}18` }}
      data-testid={`aegis-row-${testKey}`}>
      <div className="pt-1.5">
        <p className="font-['Cormorant_Garamond'] text-[12px] leading-tight" style={{ color: GOLD_HI }}>
          {slot.care}
        </p>
      </div>
      <div className="relative">
        <input
          type="text"
          value={slot.product}
          onChange={(e) => onChange({ product: e.target.value })}
          placeholder="Product…"
          className="w-full outline-none rounded-md px-2 py-1.5 pr-7 font-['Outfit'] text-[11px]"
          style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33`, color: GOLD_HI }}
          maxLength={120}
          data-testid={`aegis-cell-product-${testKey}`}
        />
        <button type="button" onClick={onOpenPicker}
          className="absolute top-1/2 right-1 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ color: GOLD }}
          aria-label="Search library"
          data-testid={`aegis-cell-search-${testKey}`}>
          <Sparkles size={11}/>
        </button>
      </div>
      <div>
        <input
          type="text"
          value={slot.nutrition}
          onChange={(e) => onChange({ nutrition: e.target.value })}
          placeholder="Nutrition…"
          className="w-full outline-none rounded-md px-2 py-1.5 font-['Outfit'] text-[11px]"
          style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33`, color: GOLD_HI }}
          maxLength={160}
          data-testid={`aegis-cell-nutrition-${testKey}`}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   SECTION (AM / PM / Weekly)
   ═══════════════════════════════════════════════════════════════════ */
const Section = ({ title, subtitle, Icon, rows, category, onUpdateRow, onOpenPicker, testid }) => (
  <div className="rounded-xl overflow-hidden"
    style={{ border: `1px solid ${GOLD}44`, background: NAVY_HI }}
    data-testid={testid}>
    <div className="flex items-center gap-3 px-3 py-2"
      style={{ background: `${GOLD}0F`, borderBottom: `1px solid ${GOLD}22` }}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}66` }}>
        <Icon size={13} color={GOLD_HI}/>
      </div>
      <div className="min-w-0">
        <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>{title}</p>
        <p className="font-['Cormorant_Garamond'] text-sm leading-tight" style={{ color: GOLD_HI }}>{subtitle}</p>
      </div>
    </div>
    {/* Column header */}
    <div className="grid grid-cols-[70px_1fr_1fr] gap-1.5 px-1 py-1.5"
      style={{ background: `${GOLD}08`, borderBottom: `1px solid ${GOLD}22` }}>
      <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.18em] pl-1" style={{ color: `${GOLD}CC` }}>Care</p>
      <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.18em]" style={{ color: `${GOLD}CC` }}>Product</p>
      <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.18em]" style={{ color: `${GOLD}CC` }}>Nutrition</p>
    </div>
    {rows.map((slot) => (
      <TableRow key={slot.id} slot={slot} category={category}
        onChange={(patch) => onUpdateRow(slot.id, patch)}
        onOpenPicker={() => onOpenPicker(slot.id)}/>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   LIBRARY PICKER (modal opened from a row search icon)
   ═══════════════════════════════════════════════════════════════════ */
const LibraryPicker = ({ open, onClose, category, targetLabel, onPick }) => {
  const [q, setQ] = useState('');
  const options = useMemo(() => searchLibrary(q, category), [q, category]);
  useEffect(() => { if (open) setQ(''); }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[160] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
          onClick={onClose}
          data-testid="aegis-picker-modal">
          <motion.div
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl p-4 max-h-[75vh] flex flex-col"
            style={{
              background: `linear-gradient(165deg, ${NAVY_HI}, ${NAVY})`,
              border: `1px solid ${GOLD}77`,
              boxShadow: `0 0 30px ${GOLD}44`,
            }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>Library Search</p>
                <h4 className="font-['Cormorant_Garamond'] text-lg leading-tight" style={{ color: GOLD_HI }}>{targetLabel}</h4>
              </div>
              <button type="button" onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ border: `1px solid ${GOLD}55`, color: GOLD }}
                data-testid="aegis-picker-close">
                <X size={13}/>
              </button>
            </div>
            <input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${category} entries…`}
              className="mt-3 outline-none rounded-lg px-3 py-2 font-['Outfit'] text-[13px]"
              style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${GOLD}44`, color: GOLD_HI }}
              data-testid="aegis-picker-search"/>
            <div className="mt-3 flex-1 overflow-y-auto space-y-1.5">
              {options.length === 0 ? (
                <p className="text-center py-6 font-['Cormorant_Garamond'] italic text-sm" style={{ color: `${GOLD}88` }}>
                  No matches. Type any product freely in the row.
                </p>
              ) : options.map((e) => {
                const isProblem = e.Type === 'Problem';
                return (
                  <button key={e.Keyword} type="button" onClick={() => onPick(e)}
                    className="w-full text-left rounded-lg p-2"
                    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33` }}
                    data-testid={`aegis-picker-item-${e.Keyword.replace(/\s+/g, '-').toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-['Outfit'] text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded"
                        style={{
                          background: isProblem ? '#B2222218' : `${GOLD}18`,
                          color: isProblem ? '#FF9090' : GOLD_HI,
                          border: `1px solid ${isProblem ? '#B2222266' : `${GOLD}66`}`,
                        }}>
                        {isProblem ? 'Restore' : 'Elevate'}
                      </span>
                      <span className="font-['Cormorant_Garamond'] text-[13px]" style={{ color: GOLD_HI }}>{e.Keyword}</span>
                    </div>
                    <p className="mt-0.5 font-['Outfit'] text-[10px] leading-snug" style={{ color: `${GOLD_HI}AA` }}>
                      {e.Chemical_Solution}
                    </p>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   FORGE SECTION — cycle picker + calendar grid
   ═══════════════════════════════════════════════════════════════════ */
const ForgeSection = ({ forge, onForge, onFuseDay, onReset }) => {
  if (!forge) {
    return (
      <div className="rounded-xl p-3"
        style={{ border: `1px dashed ${GOLD}66`, background: `${GOLD}08` }}
        data-testid="aegis-forge-picker">
        <div className="flex items-center gap-2">
          <Hammer size={13} color={GOLD}/>
          <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.24em]" style={{ color: GOLD }}>
            Forge This Regimen
          </p>
        </div>
        <p className="mt-1 font-['Cormorant_Garamond'] italic text-[12px]" style={{ color: `${GOLD}AA` }}>
          Commit your regimen to a cycle — days lock in as you fuse them.
        </p>
        <div className="mt-2.5 grid grid-cols-3 gap-1.5">
          {FORGE_PERIODS.map((p) => (
            <button key={p.days} type="button" onClick={() => onForge(p.days)}
              className="rounded-lg py-1.5 font-['Outfit'] uppercase text-[10px] tracking-[0.18em]"
              style={{
                background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
                color: '#050507',
                boxShadow: `0 0 8px ${GOLD}66`,
              }}
              data-testid={`aegis-forge-${p.days}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const total = forge.totalDays;
  const fused = forge.fusedDays || {};
  const fusedCount = Object.keys(fused).length;
  const complete = fusedCount >= total;

  return (
    <div className="rounded-xl p-3"
      style={{ border: `1px solid ${GOLD}66`, background: NAVY_HI }}
      data-testid="aegis-forge-active">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Flame size={13} color={GOLD}/>
          <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.24em]" style={{ color: GOLD }}>
            Cycle · {total} days
          </p>
        </div>
        <button type="button" onClick={onReset}
          className="w-7 h-7 rounded-full flex items-center justify-center"
          style={{ border: `1px solid ${GOLD}55`, color: `${GOLD}CC` }}
          aria-label="Reset cycle" data-testid="aegis-forge-reset">
          <RotateCcw size={11}/>
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full transition-all"
            style={{
              width: `${Math.round((fusedCount / total) * 100)}%`,
              background: `linear-gradient(90deg, ${GOLD_LO}, ${GOLD}, ${GOLD_HI})`,
              boxShadow: `0 0 8px ${GOLD}AA`,
            }}/>
        </div>
        <span className="font-['Outfit'] text-[10px]" style={{ color: GOLD_HI }} data-testid="aegis-forge-progress">
          {fusedCount} / {total}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-7 sm:grid-cols-10 gap-1" data-testid="aegis-forge-grid">
        {Array.from({ length: total }).map((_, i) => {
          const day = i + 1;
          const isFused = !!fused[day];
          return (
            <ForgeDayCell key={day} day={day} isFused={isFused} onFuse={onFuseDay}/>
          );
        })}
      </div>

      {complete && (
        <div className="mt-3 rounded-lg py-2 text-center"
          style={{ border: `1px solid ${GOLD_HI}`, background: `${GOLD}18`, boxShadow: `0 0 16px ${GOLD}55` }}
          data-testid="aegis-forge-complete">
          <p className="font-['Cormorant_Garamond'] text-base" style={{ color: GOLD_HI }}>
            Cycle Complete ✦
          </p>
          <p className="font-['Cormorant_Garamond'] italic text-xs" style={{ color: `${GOLD}CC` }}>
            {total} days sealed. Reset to run the next.
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN SCHEDULE VIEW (category-scoped)
   ═══════════════════════════════════════════════════════════════════ */
export const AegisSchedule = ({ category, onBack }) => {
  const [regimen, setRegimen] = useState(() => loadRegimen());
  const [savedFlash, setSavedFlash] = useState(false);
  const [picker, setPicker] = useState(null); // { group, slotId, label }

  useEffect(() => { saveRegimen(regimen); }, [regimen]);

  const catData = regimen[category];

  const updateSlot = useCallback((group, slotId, patch) => {
    setRegimen((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [group]: prev[category][group].map((s) => s.id === slotId ? { ...s, ...patch } : s),
      },
    }));
  }, [category]);

  const openPicker = useCallback((group, slotId) => {
    const slot = catData[group].find((s) => s.id === slotId);
    setPicker({ group, slotId, label: slot ? slot.care : '' });
  }, [catData]);

  const applyPick = (entry) => {
    if (!picker) return;
    updateSlot(picker.group, picker.slotId, {
      product: entry.Chemical_Solution,
      nutrition: entry.Nutritional_Solution,
    });
    setPicker(null);
    haptic(20);
  };

  const forge = useCallback((totalDays) => {
    setRegimen((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        forge: { totalDays, startDate: new Date().toISOString(), fusedDays: {} },
      },
    }));
    haptic(30);
  }, [category]);

  const fuseDay = useCallback((day) => {
    setRegimen((prev) => {
      const cur = prev[category].forge;
      if (!cur) return prev;
      const fused = { ...(cur.fusedDays || {}) };
      if (fused[day]) return prev; // permanent — once fused, cannot be un-fused
      fused[day] = new Date().toISOString();
      pingAuraFuse('aegis');
      return {
        ...prev,
        [category]: { ...prev[category], forge: { ...cur, fusedDays: fused } },
      };
    });
    haptic(30);
  }, [category]);

  const resetForge = useCallback(() => {
    if (!window.confirm('Reset this cycle? All fused days will be cleared.')) return;
    setRegimen((prev) => ({
      ...prev,
      [category]: { ...prev[category], forge: null },
    }));
  }, [category]);

  const saveNow = () => {
    saveRegimen(regimen);
    setSavedFlash(true);
    haptic(15);
    setTimeout(() => setSavedFlash(false), 1400);
  };

  const clearCategory = () => {
    if (!window.confirm(`Clear the entire ${category} regimen? Forge cycle will also reset.`)) return;
    setRegimen((prev) => {
      const fresh = buildInitial()[category];
      return { ...prev, [category]: fresh };
    });
  };

  const titleCat = category === 'skin' ? 'Skin'
                 : category === 'hair' ? 'Hair'
                 : 'Body';
  const amSubtitle = category === 'skin' ? 'Morning ritual for the vessel'
                   : category === 'hair' ? 'Morning maintenance for the mane'
                   : 'Morning body-care ritual';
  const pmSubtitle = category === 'skin' ? 'Evening restoration'
                   : category === 'hair' ? 'Evening treatment & repair'
                   : 'Evening body restoration';
  const weeklySubtitle = category === 'skin' ? 'Deep-work exfoliation & hydration'
                       : category === 'hair' ? 'Deep scalp treatment & oiling'
                       : 'Deep exfoliation, dry brushing & body oil';

  return (
    <div className="relative w-full h-full flex flex-col" data-testid={`aegis-schedule-view-${category}`}>
      <div className="px-4 pt-3 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onBack}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ border: `1px solid ${GOLD}55`, color: GOLD }}
            aria-label="Back"
            data-testid="aegis-schedule-back">
            <ChevronLeft size={15}/>
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD }}>
              {titleCat} Regimen Schedule
            </p>
            <h3 className="font-['Cormorant_Garamond'] text-xl leading-tight" style={{ color: GOLD_HI }}>
              Daily · Weekly · Forge
            </h3>
          </div>
          <button type="button" onClick={saveNow}
            className="px-3 py-1.5 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.22em] flex items-center gap-1"
            style={{
              background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD_LO})`,
              color: '#050507', boxShadow: `0 0 10px ${GOLD}77`,
            }}
            data-testid="aegis-schedule-save">
            <Save size={11}/> {savedFlash ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-24 space-y-3">
        <Section
          testid={`aegis-section-am-${category}`}
          title="AM · Protect & Shield"
          subtitle={amSubtitle}
          Icon={Sunrise}
          rows={catData.am} category={category}
          onUpdateRow={(id, patch) => updateSlot('am', id, patch)}
          onOpenPicker={(id) => openPicker('am', id)}/>
        <Section
          testid={`aegis-section-pm-${category}`}
          title="PM · Restore & Repair"
          subtitle={pmSubtitle}
          Icon={Moon}
          rows={catData.pm} category={category}
          onUpdateRow={(id, patch) => updateSlot('pm', id, patch)}
          onOpenPicker={(id) => openPicker('pm', id)}/>
        <Section
          testid={`aegis-section-weekly-${category}`}
          title="Weekly · Intensive Upgrades"
          subtitle={weeklySubtitle}
          Icon={CalendarRange}
          rows={catData.weekly} category={category}
          onUpdateRow={(id, patch) => updateSlot('weekly', id, patch)}
          onOpenPicker={(id) => openPicker('weekly', id)}/>

        <ForgeSection
          forge={catData.forge}
          onForge={forge}
          onFuseDay={fuseDay}
          onReset={resetForge}/>

        <button type="button" onClick={clearCategory}
          className="w-full py-2 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.22em]"
          style={{ border: `1px solid ${GOLD}44`, color: `${GOLD}CC` }}
          data-testid="aegis-schedule-clear">
          Clear {titleCat} Regimen
        </button>
      </div>

      <LibraryPicker
        open={!!picker}
        onClose={() => setPicker(null)}
        category={category}
        targetLabel={picker ? picker.label : ''}
        onPick={applyPick}/>
    </div>
  );
};

export default AegisSchedule;
